import React, { useState, useEffect, useId, useMemo } from "react";
import { Link } from "react-router-dom";
import type { CatalogEntry, BillingInterval } from "../services/accountService";
import {
  formatCurrency,
  formatCurrencyWhole,
  getBillingIntervalLabel,
} from "../utils/billingFormat";

const BILLING_INTERVALS: BillingInterval[] = ["monthly", "quarterly", "yearly"];

interface CatalogPlanSelectorProps {
  entries: CatalogEntry[];
  onSelect: (productId: string, billingInterval: BillingInterval) => void;
  loading?: boolean;
  disabled?: boolean;
  /** Billing interval of the existing pending order, if any (AC-4). Radio for this interval is disabled. */
  currentOrderInterval?: BillingInterval | null;
  /** Proforma number (e.g. "P17/05/2026") or order number for the existing order (AC-4). */
  currentOrderNumber?: string | null;
  /**
   * AC-16: Enabled modules of the current subscription.
   * Used to determine which SKU is currently owned (highlight) and show upgrade CTA.
   * grants:kfs only → Standard KFS owned; grants:bur only → Standard BUR owned;
   * both → pełny Standard owned.
   */
  enabledModules?: string[];
  /**
   * AC-15: price source for the renewal-preference modal (`renewal-options`), keyed per
   * product × interval — takes priority over `metadata.priceMonthly` when provided, and is
   * re-evaluated for the currently selected interval (unlike the metadata price, which is
   * always monthly regardless of the selected interval). When provided but no price is found
   * for the current entry/interval, "cena chwilowo niedostępna" is shown instead of nothing.
   */
  priceOverride?: (
    entry: CatalogEntry,
    billingInterval: BillingInterval
  ) => { netCents: number; grossCents: number; currency: string } | null;
  /** AC-15: notifies the parent of the current (entry, interval) selection as the user picks, before submit. */
  onSelectionChange?: (productId: string, billingInterval: BillingInterval) => void;
  /** Override the submit button's default "Zamów dostęp" copy (AC-15 reuses this component for a different action). */
  submitLabel?: string;
  /** Override the submit button's default "Tworzenie zamówienia..." loading copy. */
  submitLoadingLabel?: string;
  /**
   * AC-15: id of the tenant's currently active package. Shows a non-blocking "Obecny pakiet"
   * badge — unlike `enabledModules`/`ownedProductId`, it never disables the card (the renewal
   * modal must let the tenant re-confirm or pick a narrower version of their current package).
   */
  currentProductId?: string | null;
  /** AC-15: pre-selects this product on mount when present in `entries` (e.g. a stored renewal preference). */
  initialSelectedProductId?: string | null;
  /** AC-15: pre-selects this billing interval on mount (paired with `initialSelectedProductId`). */
  initialSelectedInterval?: BillingInterval | null;
}

/**
 * CatalogPlanSelector Component
 *
 * Displays catalog entries from the billing catalog API for plan selection.
 * Used for ordering-model subscriptions (post-migration) where pricing is
 * calculated on order creation, not displayed upfront.
 *
 * Unlike PlanSelector (legacy Stripe flow), this uses productId + billingInterval
 * instead of planId + stripePriceId.
 */
export const CatalogPlanSelector: React.FC<CatalogPlanSelectorProps> = ({
  entries,
  onSelect,
  loading = false,
  disabled = false,
  currentOrderInterval = null,
  currentOrderNumber = null,
  enabledModules,
  priceOverride,
  onSelectionChange,
  submitLabel,
  submitLoadingLabel,
  currentProductId = null,
  initialSelectedProductId = null,
  initialSelectedInterval = null,
}) => {
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  // M9: If there's an existing pending order, the radio for its interval is disabled.
  // Default-selecting that disabled interval would let the user click "Zamów dostęp"
  // and hit a backend idempotent HTTP 200 (no-op from their perspective). Lazy
  // initializer picks the first interval different from the current order.
  // AC-15: `initialSelectedInterval` (e.g. a stored renewal preference) takes priority,
  // unless it collides with the disabled `currentOrderInterval`.
  const [selectedInterval, setSelectedInterval] = useState<BillingInterval>(() => {
    if (initialSelectedInterval && initialSelectedInterval !== currentOrderInterval) {
      return initialSelectedInterval;
    }
    if (currentOrderInterval) {
      const firstAvailable = BILLING_INTERVALS.find((i) => i !== currentOrderInterval);
      if (firstAvailable) return firstAvailable;
    }
    return "monthly";
  });
  // L5: Unique id per instance avoids collisions when component is rendered
  // more than once on the same page (e.g., in a modal alongside the page).
  const labelId = useId();

  // Auto-select first entry on mount — AC-15: prefer `initialSelectedProductId` when present.
  useEffect(() => {
    if (entries.length > 0 && !selectedEntryId) {
      const preferred =
        initialSelectedProductId &&
        entries.some((e) => e.productId === initialSelectedProductId)
          ? initialSelectedProductId
          : entries[0].productId;
      setSelectedEntryId(preferred);
    }
  }, [entries, selectedEntryId, initialSelectedProductId]);

  // AC-15: let the parent (renewal-preference modal) react to the in-progress selection
  // (e.g. to show a "Utracisz: {moduły}" notice) before the submit button is clicked.
  useEffect(() => {
    if (selectedEntryId && onSelectionChange) {
      onSelectionChange(selectedEntryId, selectedInterval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntryId, selectedInterval]);

  // AC-16 + MEDIUM-9: Single memo computes ownedProductId, hasNarrowScheme, and
  // fullStandardEntry in one pass instead of three separate per-render calculations.
  const { ownedProductId, hasNarrowScheme, fullStandardEntry } = useMemo(() => {
    if (!enabledModules) {
      return { ownedProductId: null, hasNarrowScheme: false, fullStandardEntry: null };
    }

    const hasKfs = enabledModules.some((m) => m === "grants:kfs" || m === "grants:*");
    const hasBur = enabledModules.some((m) => m === "grants:bur" || m === "grants:*");

    let computedOwnedProductId: string | null = null;
    let computedFullStandardEntry: typeof entries[number] | null = null;

    for (const entry of entries) {
      const modules = entry.metadata?.modules;
      if (!Array.isArray(modules)) continue;
      const entryHasKfs = modules.includes("grants:kfs");
      const entryHasBur = modules.includes("grants:bur");

      // Match: entry has exactly the modules the tenant has
      if (!computedOwnedProductId) {
        if (entryHasKfs && entryHasBur && hasKfs && hasBur) computedOwnedProductId = entry.productId;
        else if (entryHasKfs && !entryHasBur && hasKfs && !hasBur) computedOwnedProductId = entry.productId;
        else if (!entryHasKfs && entryHasBur && !hasKfs && hasBur) computedOwnedProductId = entry.productId;
      }

      // Find full Standard (both KFS+BUR) for upgrade CTA
      if (!computedFullStandardEntry && entryHasKfs && entryHasBur) {
        computedFullStandardEntry = entry;
      }
    }

    const computedNarrowScheme =
      (enabledModules.includes("grants:kfs") || enabledModules.includes("grants:bur")) &&
      !enabledModules.includes("grants:*") &&
      !(enabledModules.includes("grants:kfs") && enabledModules.includes("grants:bur"));

    return {
      ownedProductId: computedOwnedProductId,
      hasNarrowScheme: computedNarrowScheme,
      fullStandardEntry: computedFullStandardEntry,
    };
  }, [entries, enabledModules]);

  const handleSelectClick = () => {
    if (selectedEntryId) {
      onSelect(selectedEntryId, selectedInterval);
    }
  };

  if (entries.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Brak dostępnych planów.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* AC-16: Upgrade CTA for narrow module tenants (KFS-only or BUR-only) */}
      {hasNarrowScheme && fullStandardEntry && (
        <div
          data-testid="upgrade-cta-full-standard"
          className="flex items-start gap-3 p-4 bg-primary-50 border border-primary-200 rounded-lg"
          role="note"
        >
          <div className="flex-1">
            <p className="text-sm font-medium text-primary-800">
              Chcesz mieć dostęp do obu schematów?
            </p>
            <p className="text-sm text-primary-800 mt-1">
              Przejdź na{" "}
              <strong>{fullStandardEntry.displayName}</strong> i uzyskaj pełny dostęp do KFS i BUR.
            </p>
            <button
              type="button"
              onClick={() => !disabled && onSelect(fullStandardEntry.productId, selectedInterval)}
              disabled={disabled || loading}
              className="mt-2 text-sm font-medium text-primary-800 underline hover:text-primary-900 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="upgrade-cta-full-standard-btn"
            >
              Wybierz {fullStandardEntry.displayName}
            </button>
          </div>
        </div>
      )}

      {/* Plan Cards */}
      <div className="space-y-4" role="radiogroup" aria-label="Wybierz plan">
        {entries.map((entry) => {
          const isSelected = selectedEntryId === entry.productId;
          const isOwned = ownedProductId === entry.productId;
          // AC-15: non-blocking "current package" badge — distinct from `isOwned`, which also
          // disables the card (unwanted here: the modal must allow re-confirming/narrowing it).
          const isCurrentPackage = !isOwned && currentProductId === entry.productId;
          // AC-15: renewal-options price takes priority over metadata.priceMonthly when provided.
          const overriddenPrice = priceOverride?.(entry, selectedInterval) ?? null;
          const priceUnavailable = Boolean(priceOverride) && !overriddenPrice;
          const priceMonthly = typeof entry.metadata?.priceMonthly === "number"
            ? entry.metadata.priceMonthly
            : null;
          const priceDisplay = overriddenPrice
            ? formatCurrency(overriddenPrice.netCents, overriddenPrice.currency)
            : priceMonthly !== null
            ? formatCurrencyWhole(priceMonthly, "PLN")
            : null;

          return (
            <div
              key={entry.id}
              onClick={() => !disabled && !isOwned && setSelectedEntryId(entry.productId)}
              className={`border-2 rounded-lg p-4 transition ${
                isOwned
                  ? "border-green-500 bg-green-50 cursor-default"
                  : isSelected
                    ? "border-blue-500 bg-blue-50 cursor-pointer"
                    : "border-gray-200 hover:border-gray-300 cursor-pointer"
              } ${disabled && !isOwned ? "opacity-50 cursor-not-allowed" : ""}`}
              role="radio"
              aria-checked={isSelected}
              aria-label={`${entry.displayName}${isOwned ? " — aktywny plan" : ""}`}
              tabIndex={isOwned ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (!disabled && !isOwned) setSelectedEntryId(entry.productId);
                }
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {entry.displayName}
                  </h3>
                  {entry.marketingDescription && (
                    <p className="text-sm text-gray-600 mt-1">
                      {entry.marketingDescription}
                    </p>
                  )}
                  {priceDisplay && (
                    <p className="text-sm font-medium text-gray-700 mt-2">
                      {overriddenPrice ? (
                        `${priceDisplay} netto`
                      ) : (
                        <>
                          {priceDisplay}
                          <span className="text-xs font-normal text-gray-500">/mc</span>
                        </>
                      )}
                    </p>
                  )}
                  {!priceDisplay && priceUnavailable && (
                    <p className="text-sm text-gray-500 mt-2">cena chwilowo niedostępna</p>
                  )}
                </div>
                {/* AC-16: owned plan badge */}
                {isOwned && (
                  <span
                    data-testid="owned-plan-badge"
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap"
                  >
                    Twój plan
                  </span>
                )}
                {/* AC-15: non-blocking current-package badge for the renewal-preference modal */}
                {isCurrentPackage && (
                  <span
                    data-testid="current-package-badge"
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap"
                  >
                    Obecny pakiet
                  </span>
                )}
              </div>
              {/* AC-16: locked-state for non-owned plans when tenant has a plan */}
              {ownedProductId && !isOwned && (
                <div
                  data-testid={`locked-plan-${entry.productId}`}
                  className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
                    <path fillRule="evenodd" d="M8 1a3.5 3.5 0 0 0-3.5 3.5V7A1.5 1.5 0 0 0 3 8.5v4A1.5 1.5 0 0 0 4.5 14h7a1.5 1.5 0 0 0 1.5-1.5v-4A1.5 1.5 0 0 0 11 7V4.5A3.5 3.5 0 0 0 8 1Zm2 6V4.5a2 2 0 1 0-4 0V7h4Z" clipRule="evenodd" />
                  </svg>
                  <span>
                    Dostępny po{" "}
                    <Link to="/tenant/subscription" className="text-primary-700 hover:underline">
                      zmianie planu
                    </Link>
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Billing Interval Selector */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">
          Interwał rozliczeniowy
        </h3>
        {/* Label for current order — referenced via aria-describedby on the disabled radio (AC-4) */}
        {/* L6: text-gray-600 (~5.7:1) instead of text-gray-500 (~4.6:1) — comfortably above WCAG AA. */}
        {/* L5: id is generated via useId() to avoid collisions across instances. */}
        {currentOrderInterval && currentOrderNumber && (
          <p
            id={labelId}
            className="text-xs text-gray-600 mb-2"
          >
            Aktualne zamówienie ({currentOrderNumber})
          </p>
        )}
        <div
          className="flex flex-wrap gap-2"
          role="radiogroup"
          aria-label="Interwał rozliczeniowy"
        >
          {BILLING_INTERVALS.map((interval) => {
            const isActive = interval === selectedInterval;
            const isCurrentOrder = interval === currentOrderInterval;
            const isDisabled = disabled || isCurrentOrder;
            // H12: only reference labelId when the label is actually rendered
            // (requires BOTH currentOrderInterval AND currentOrderNumber). When
            // we have the interval but no number, fall back to a plain aria-label
            // so the screen reader still announces context.
            const hasLabelElement = isCurrentOrder && Boolean(currentOrderNumber);
            const fallbackAriaLabel =
              isCurrentOrder && !currentOrderNumber
                ? "Istnieje już zamówienie dla tego interwału"
                : undefined;
            return (
              <button
                key={interval}
                role="radio"
                aria-checked={isActive}
                aria-describedby={hasLabelElement ? labelId : undefined}
                aria-label={fallbackAriaLabel}
                onClick={() => !isDisabled && setSelectedInterval(interval)}
                disabled={isDisabled}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
                  isActive
                    ? "border-primary-500 bg-primary-50 text-primary-800"
                    : "border-gray-300 bg-white text-gray-700 hover:border-primary-400 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                }`}
              >
                {getBillingIntervalLabel(interval)}
              </button>
            );
          })}
        </div>
        {/* M8: Explain the exit — picking a non-disabled interval replaces the existing order. */}
        {currentOrderInterval && (
          <p className="text-xs text-gray-600 mt-2">
            Wybierz inny interwał, aby zastąpić aktualne zamówienie nowym.
          </p>
        )}
      </div>

      {/* Select Button */}
      <button
        onClick={handleSelectClick}
        disabled={!selectedEntryId || loading || disabled}
        aria-busy={loading}
        className="w-full md:w-auto px-6 py-3 bg-primary-700 text-white font-semibold rounded-lg hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
      >
        {loading
          ? submitLoadingLabel ?? "Tworzenie zamówienia..."
          : submitLabel ?? "Zamów dostęp"}
      </button>

      {/* Pricing note — L-2: hidden when `priceOverride` is passed (renewal modal already shows the net price). */}
      {!priceOverride && (
        <p className="text-sm text-gray-500 text-center">
          Szczegóły cenowe zostaną zawarte w proformie.
        </p>
      )}
    </div>
  );
};
